/**
 * Générateur de feuilles jumelles : rejoue une feuille CSS en entier, dans l'ordre, sans toucher
 * aux sélecteurs, en ne convertissant que les longueurs de ses valeurs (`Npx` -> `calc(N * <px>)`
 * si |N| > filet, `Xvw` -> `calc(X * <vw>)`). Aucune connaissance de la landing : exceptions et
 * portes sont fournies par `TwinOptions`. Aucune importation.
 *
 * Forme canonique de la sortie de `twinSheet` :
 * - règle de style : `S{p1:v1;p2:v2}` (sélecteurs normalisés, valeurs rognées et espaces réduites
 *   hors chaînes/`url()`, sans `;` final ni déclaration vide ; règle sans déclaration -> `S{}`) ;
 * - bloc d'at-rule conservé : `@nom P{…}` (`P` rogné, jamais converti ; `@nom{…}` si vide) ;
 * - éléments frères séparés par `\n`, sans `\n` initial ni final.
 *
 * Retirés : commentaires, `@keyframes` (préfixés compris), `@font-face`, `@media` rejetés par
 * `dropMedia`. Imbriqués et rejoués : `@media`, `@supports`, `@starting-style`.
 *
 * Erreurs (`Error`, message `twinSheet : …`) : bloc non fermé, accolade fermante orpheline,
 * prélude sans bloc, at-rule non prise en charge, règle imbriquée non prise en charge.
 */

/** Options de la jumelle. */
export interface TwinOptions {
	/** Expression insérée pour 1px : `calc(N * ${px})`. */
	readonly px: string;
	/** Expression insérée pour 1vw : `calc(X * ${vw})`. */
	readonly vw: string;
	/** Filet : un `Npx` avec |N| <= hairline reste tel quel. */
	readonly hairline: number;
	/** Règles chrome : si chaque sélecteur de la liste correspond, la règle est recopiée sans conversion. */
	readonly chrome: RegExp;
	/** Déclarations `[sélecteur normalisé, propriété]` recopiées sans conversion. */
	readonly keep: readonly (readonly [selector: string, property: string])[];
	/** Déclarations `[sélecteur normalisé, propriété]` retirées. */
	readonly omit: readonly (readonly [selector: string, property: string])[];
	/** Reçoit le prélude rogné d'un `@media` ; vrai -> bloc retiré. */
	readonly dropMedia: (prelude: string) => boolean;
}

/** Sous-ensemble des options suffisant à convertir une valeur isolée. */
export type LengthOptions = Pick<TwinOptions, "px" | "vw" | "hairline">;

const TOKEN = /(?<![\w.-])([+-]?(?:\d+(?:\.\d*)?|\.\d+))(px|vw)(?![\w-])/g;

/** Index juste après la chaîne quotée qui s'ouvre en `i` (échappements respectés ; fin de texte tolérée). */
function skipString(s: string, i: number): number {
	const quote = s.charAt(i);
	let j = i + 1;
	while (j < s.length) {
		const c = s.charAt(j);
		if (c === "\\") j += 2;
		else if (c === quote) return j + 1;
		else j++;
	}
	return s.length;
}

/** Index juste après l'`url(…)` qui s'ouvre en `i` (`)` fermante hors chaîne). */
function skipUrl(s: string, i: number): number {
	let j = i + 4;
	while (j < s.length) {
		const c = s.charAt(j);
		if (c === '"' || c === "'") j = skipString(s, j);
		else if (c === ")") return j + 1;
		else j++;
	}
	return s.length;
}

/** Découpe en segments `[texte, protégé]` : chaînes et `url(…)` protégés. */
function segments(value: string): [string, boolean][] {
	const out: [string, boolean][] = [];
	let free = 0;
	let i = 0;
	const flush = (end: number): void => {
		if (end > free) out.push([value.slice(free, end), false]);
	};
	while (i < value.length) {
		const c = value.charAt(i);
		let end = -1;
		if (c === '"' || c === "'") end = skipString(value, i);
		else if ((c === "u" || c === "U") && /^url\(/i.test(value.slice(i, i + 4)) && !/[\w-]/.test(value.charAt(i - 1))) end = skipUrl(value, i);
		if (end === -1) {
			i++;
			continue;
		}
		flush(i);
		out.push([value.slice(i, end), true]);
		i = end;
		free = end;
	}
	flush(value.length);
	return out;
}

/**
 * Convertit les longueurs `px`/`vw` d'une valeur (chaînes et `url()` intacts, espaces inchangées).
 *
 * @param value valeur de déclaration
 * @param opts expressions de remplacement et filet
 * @returns la valeur convertie
 */
export function scaleLengths(value: string, opts: LengthOptions): string {
	return segments(value)
		.map(([text, isProtected]) =>
			isProtected
				? text
				: text.replace(TOKEN, (token: string, n: string, unit: string) => {
						if (unit === "vw") return `calc(${n} * ${opts.vw})`;
						return Math.abs(Number(n)) <= opts.hairline ? token : `calc(${n} * ${opts.px})`;
					}),
		)
		.join("");
}

/** Réduit les suites d'espaces blancs hors chaînes et `url()`. */
function collapseSpaces(value: string): string {
	return segments(value)
		.map(([text, isProtected]) => (isProtected ? text : text.replace(/\s+/g, " ")))
		.join("");
}

/** Retire les commentaires hors chaînes ; commentaire non fermé : le reste est un commentaire. */
function stripComments(css: string): string {
	let out = "";
	let i = 0;
	while (i < css.length) {
		const c = css.charAt(i);
		if (c === '"' || c === "'") {
			const end = skipString(css, i);
			out += css.slice(i, end);
			i = end;
		} else if (c === "/" && css.charAt(i + 1) === "*") {
			const end = css.indexOf("*/", i + 2);
			if (end === -1) break;
			i = end + 2;
		} else {
			out += c;
			i++;
		}
	}
	return out;
}

/** Découpe `s` aux `sep` de premier niveau (hors chaînes, parenthèses et crochets) ; morceaux sans le séparateur, vides compris. */
function splitTopLevel(s: string, sep: string): string[] {
	const parts: string[] = [];
	let depth = 0;
	let start = 0;
	let i = 0;
	while (i < s.length) {
		const c = s.charAt(i);
		if (c === '"' || c === "'") {
			i = skipString(s, i);
			continue;
		}
		if (c === "(" || c === "[") depth++;
		else if (c === ")" || c === "]") depth--;
		else if (c === sep && depth <= 0) {
			parts.push(s.slice(start, i));
			start = i + 1;
		}
		i++;
	}
	parts.push(s.slice(start));
	return parts;
}

/** Index du premier `:` de premier niveau, ou -1. */
function colonIndex(decl: string): number {
	let depth = 0;
	let i = 0;
	while (i < decl.length) {
		const c = decl.charAt(i);
		if (c === '"' || c === "'") {
			i = skipString(decl, i);
			continue;
		}
		if (c === "(") depth++;
		else if (c === ")") depth--;
		else if (c === ":" && depth <= 0) return i;
		i++;
	}
	return -1;
}

const pairIn = (list: readonly (readonly [string, string])[], selector: string, prop: string): boolean =>
	list.some(([s, p]) => s === selector && p === prop);

/**
 * Rejoue une feuille CSS en forme canonique avec conversion des longueurs de ses valeurs.
 *
 * @param css texte de la feuille
 * @param opts options de la jumelle
 * @returns la feuille jumelle canonique
 * @throws Error `twinSheet : …` sur feuille mal formée ou construction non prise en charge
 */
export function twinSheet(css: string, opts: TwinOptions): string {
	const src = stripComments(css);
	// Copie sans g/y : `test` sans état (lastIndex).
	const chrome = new RegExp(opts.chrome.source, opts.chrome.flags.replace(/[gy]/g, ""));
	let i = 0;

	/** Avance `i` juste après l'accolade fermante du bloc ouvert (accolades équilibrées, chaînes sautées) ; lève `twinSheet : bloc non fermé` si la feuille s'arrête avant. */
	const skipBlock = (): void => {
		let depth = 1;
		while (i < src.length) {
			const c = src.charAt(i);
			if (c === '"' || c === "'") {
				i = skipString(src, i);
				continue;
			}
			i++;
			if (c === "{") depth++;
			else if (c === "}" && --depth === 0) return;
		}
		throw new Error("twinSheet : bloc non fermé");
	};

	const styleRule = (selector: string): string => {
		const bodyStart = i;
		let j = i;
		while (j < src.length) {
			const c = src.charAt(j);
			if (c === '"' || c === "'") {
				j = skipString(src, j);
				continue;
			}
			if (c === "{") throw new Error("twinSheet : règle imbriquée non prise en charge");
			if (c === "}") break;
			j++;
		}
		if (j >= src.length) throw new Error("twinSheet : bloc non fermé");
		i = j + 1;
		const sel = selector.trim().replace(/\s+/g, " ").replace(/\s*,\s*/g, ",");
		const isChrome = splitTopLevel(sel, ",").every((entry) => chrome.test(entry.trim()));
		const decls: string[] = [];
		for (const raw of splitTopLevel(src.slice(bodyStart, j), ";")) {
			const decl = raw.trim();
			if (decl === "") continue;
			const colon = colonIndex(decl);
			if (colon === -1) {
				decls.push(decl);
				continue;
			}
			const prop = decl.slice(0, colon).trim();
			if (pairIn(opts.omit, sel, prop)) continue;
			const value = collapseSpaces(decl.slice(colon + 1).trim());
			decls.push(`${prop}:${isChrome || pairIn(opts.keep, sel, prop) ? value : scaleLengths(value, opts)}`);
		}
		return `${sel}{${decls.join(";")}}`;
	};

	const items = (depth: number): string[] => {
		const out: string[] = [];
		for (;;) {
			while (i < src.length && /\s/.test(src.charAt(i))) i++;
			if (i >= src.length) {
				if (depth > 0) throw new Error("twinSheet : bloc non fermé");
				return out;
			}
			if (src.charAt(i) === "}") {
				if (depth === 0) throw new Error("twinSheet : accolade fermante orpheline");
				i++;
				return out;
			}
			// Prélude jusqu'au `{` ou `;` de premier niveau.
			const start = i;
			let paren = 0;
			while (i < src.length) {
				const c = src.charAt(i);
				if (c === '"' || c === "'") {
					i = skipString(src, i);
					continue;
				}
				if (c === "(" || c === "[") paren++;
				else if (c === ")" || c === "]") paren--;
				else if (paren <= 0 && (c === "{" || c === ";" || c === "}")) break;
				i++;
			}
			const prelude = src.slice(start, i);
			const end = src.charAt(i);
			if (end !== "{" && !(end === ";" && prelude.trimStart().startsWith("@"))) {
				throw new Error(i >= src.length && depth > 0 ? "twinSheet : bloc non fermé" : "twinSheet : prélude sans bloc");
			}
			i++;
			const at = /^\s*@([\w-]+)/.exec(prelude);
			if (at === null) {
				out.push(styleRule(prelude));
				continue;
			}
			const name = (at[1] ?? "").toLowerCase();
			const rest = prelude.slice(at[0].length).trim().replace(/\s+/g, " ");
			if (end === ";") throw new Error(`twinSheet : at-rule non prise en charge : @${name}`);
			if (name === "keyframes" || name.endsWith("-keyframes") || name === "font-face") {
				skipBlock();
			} else if (name === "media" && opts.dropMedia(rest)) {
				skipBlock();
			} else if (name === "media" || name === "supports" || name === "starting-style") {
				out.push(`@${name}${rest === "" ? "" : ` ${rest}`}{${items(depth + 1).join("\n")}}`);
			} else {
				throw new Error(`twinSheet : at-rule non prise en charge : @${name}`);
			}
		}
	};

	return items(0).join("\n");
}
